import axios from "axios";
import { API_URL } from "../constants";
import {
  DeskCardCounts,
  DeskCardDetail,
  DeskCardListItem,
  DeskCardShareItem,
  DeskCardsFiltersValue,
  DeskMailboxConfig,
  DeskMailboxHealth,
  DeskMailboxRefreshResult,
  DeskPagination,
} from "../types/Desk";

export async function fetchDeskMailboxHealth(companyId: string): Promise<{ mailboxes: DeskMailboxHealth[] }> {
  const res = await axios.get(`${API_URL}/${companyId}/desk/mailboxes/health`);
  return res.data;
}

/**
 * "Check mail now". Resolves only once the worker has actually swept (or given
 * up), so the caller can reload the list and report a real result instead of
 * "queued". Without `mailboxId` every connected mailbox of the user is checked.
 */
export async function refreshDeskMail(
  companyId: string,
  mailboxId?: string
): Promise<{ mailboxes: DeskMailboxRefreshResult[] }> {
  const res = await axios.post(`${API_URL}/${companyId}/desk/mailboxes/refresh`, {
    mailboxId: mailboxId || undefined,
  });
  return res.data;
}

export async function fetchDeskCards(
  companyId: string,
  params: { page?: number; pageSize?: number; filtersValue: DeskCardsFiltersValue }
): Promise<{ entries: DeskCardListItem[]; pagination: DeskPagination }> {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/fetch`, params);
  return res.data;
}

/**
 * The mailbox picked in the list switcher. The nav badges have to agree with
 * the list under them, so both read this one place; the event is what tells the
 * nav to refetch, since the two components have no common owner.
 */
export const DESK_MAILBOX_CHANGED_EVENT = "desk:mailbox-changed";

function deskMailboxStorageKey(companyId: string): string {
  return `desk.selectedMailbox.${companyId}`;
}

export function readSelectedDeskMailbox(companyId: string): string | null {
  try {
    return localStorage.getItem(deskMailboxStorageKey(companyId));
  } catch {
    return null; // private mode / storage disabled
  }
}

export function writeSelectedDeskMailbox(companyId: string, mailboxId: string | null): void {
  try {
    // An empty string is a real choice — the combined "all mailboxes" view —
    // so only an explicit null clears the preference.
    if (mailboxId === null) {
      localStorage.removeItem(deskMailboxStorageKey(companyId));
    } else {
      localStorage.setItem(deskMailboxStorageKey(companyId), mailboxId);
    }
  } catch {
    /* storage disabled — the switcher still works for this session */
  }
  window.dispatchEvent(new CustomEvent(DESK_MAILBOX_CHANGED_EVENT));
}

/**
 * Per-tab card counts for the Desk nav badges. One round-trip for all views —
 * the nav polls it on the same interval as the lists.
 */
export async function fetchDeskCardCounts(
  companyId: string,
  mailboxId?: string | null
): Promise<DeskCardCounts> {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/counts`, {
    mailboxId: mailboxId || undefined,
  });
  return res.data;
}

export async function fetchDeskCardByNumber(companyId: string, cardNumber: number): Promise<DeskCardDetail> {
  const res = await axios.get(`${API_URL}/${companyId}/desk/cards/${cardNumber}`);
  return res.data;
}

export function deskAttachmentUrl(companyId: string, attachmentId: string): string {
  return `${API_URL}/${companyId}/desk/attachments/${attachmentId}`;
}

export async function replyToDeskCard(
  companyId: string,
  cardNumber: number,
  params: { to: string[]; cc?: string[]; text: string; keepStatus?: boolean }
) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/${cardNumber}/reply`, params);
  return res.data;
}

export async function createDeskCard(
  companyId: string,
  params: {
    title: string;
    customerEmail: string;
    customerName?: string;
    text?: string;
    mailboxId?: string;
    projectId?: string;
    clientId?: string;
    isDraft?: boolean;
  }
): Promise<{ card: { id: string; number: number; title: string; mailbox: string; isDraft: boolean } }> {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards`, params);
  return res.data;
}

export async function addDeskNote(companyId: string, cardNumber: number, text: string) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/${cardNumber}/note`, { text });
  return res.data;
}

export async function shareDeskCard(
  companyId: string,
  cardNumber: number,
  params: { userIds: string[]; text: string }
): Promise<{ shares: DeskCardShareItem[] }> {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/${cardNumber}/share`, params);
  return res.data;
}

export async function unshareDeskCard(
  companyId: string,
  cardNumber: number,
  params: { userIds: string[] }
): Promise<{ shares: DeskCardShareItem[] }> {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/${cardNumber}/unshare`, params);
  return res.data;
}

export async function updateDeskCard(
  companyId: string,
  cardNumber: number,
  updates: Record<string, unknown>
) {
  const res = await axios.patch(`${API_URL}/${companyId}/desk/cards/${cardNumber}`, updates);
  return res.data;
}

export async function deleteDeskCard(companyId: string, cardNumber: number) {
  const res = await axios.delete(`${API_URL}/${companyId}/desk/cards/${cardNumber}`);
  return res.data;
}

export async function mergeDeskCards(companyId: string, cardNumber: number, targetCardNumber: number) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/${cardNumber}/merge`, {
    targetCardNumber,
  });
  return res.data;
}

export async function summarizeDeskCard(
  companyId: string,
  cardNumber: number
): Promise<{ summary: string; summarizedAt: number }> {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/${cardNumber}/summarize`);
  return res.data;
}

/**
 * Polish the draft sitting in the composer: 'fix' corrects language mistakes
 * only, 'rewrite' restates the same message in a nicer tone. Nothing is stored
 * or sent — the improved text comes back for the user to edit and send.
 */
export async function assistDeskCompose(
  companyId: string,
  cardNumber: number,
  mode: "fix" | "rewrite",
  text: string
): Promise<{ text: string; mode: string }> {
  const res = await axios.post(`${API_URL}/${companyId}/desk/cards/${cardNumber}/compose-assist`, {
    mode,
    text,
  });
  return res.data;
}

export async function retryDeskMessage(companyId: string, messageId: string) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/messages/${messageId}/retry`, {});
  return res.data;
}

export async function fetchDeskClients(companyId: string) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/clients/fetch`, {});
  return res.data;
}

export async function fetchDeskCustomerOptions(companyId: string) {
  const res = await axios.get(`${API_URL}/${companyId}/desk/clients/customer-options`);
  return res.data;
}

export async function setDeskDayCapacity(companyId: string, date: string, points: number | null) {
  const res = await axios.put(`${API_URL}/${companyId}/desk/day-plan/${date}/capacity`, { points });
  return res.data;
}

export async function createDeskClient(
  companyId: string,
  params: { name?: string; customer?: string; domains?: string[] }
) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/clients`, params);
  return res.data;
}

export async function updateDeskClient(
  companyId: string,
  clientId: string,
  updates: Record<string, unknown>
) {
  const res = await axios.patch(`${API_URL}/${companyId}/desk/clients/${clientId}`, updates);
  return res.data;
}

export async function fetchDeskProjects(companyId: string) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/projects/fetch`, {});
  return res.data;
}

export async function createDeskProject(companyId: string, params: { client: string; name: string }) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/projects`, params);
  return res.data;
}

export async function fetchDeskDayPlan(companyId: string, date: string) {
  const res = await axios.get(`${API_URL}/${companyId}/desk/day-plan/${date}`);
  return res.data;
}

export async function saveDeskDayPlanOrder(companyId: string, date: string, entryIds: string[]) {
  const res = await axios.put(`${API_URL}/${companyId}/desk/day-plan/${date}`, { entryIds });
  return res.data;
}

export async function addDeskDayPlanEntry(
  companyId: string,
  date: string,
  params: { cardNumber: number; note?: string }
) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/day-plan/${date}/entries`, params);
  return res.data;
}

export async function updateDeskDayPlanEntry(
  companyId: string,
  entryId: string,
  updates: { done?: boolean; note?: string; points?: number | null }
) {
  const res = await axios.patch(`${API_URL}/${companyId}/desk/day-plan/entries/${entryId}`, updates);
  return res.data;
}

export async function removeDeskDayPlanEntry(companyId: string, entryId: string) {
  const res = await axios.delete(`${API_URL}/${companyId}/desk/day-plan/entries/${entryId}`);
  return res.data;
}

export async function fetchMyDeskMailbox(companyId: string): Promise<{
  mailboxes: DeskMailboxConfig[];
  mailbox: DeskMailboxConfig | null;
}> {
  const res = await axios.get(`${API_URL}/${companyId}/desk/mailboxes/mine`);
  const data = res.data || {};
  const mailboxes: DeskMailboxConfig[] = Array.isArray(data.mailboxes)
    ? data.mailboxes
    : data.mailbox
      ? [data.mailbox]
      : [];
  return {
    mailboxes,
    mailbox: mailboxes[0] || null,
  };
}

export async function saveMyDeskMailbox(companyId: string, params: Record<string, unknown>) {
  const res = await axios.put(`${API_URL}/${companyId}/desk/mailboxes/mine`, params);
  return res.data;
}

/**
 * Disconnect a mailbox. mode 'disable' wipes the stored credentials and keeps
 * the row (its cards and their history stay readable); mode 'delete' removes
 * the row outright and is only accepted while no card references it.
 */
export async function deleteMyDeskMailbox(
  companyId: string,
  mailboxId: string,
  mode: "disable" | "delete" = "disable"
): Promise<{ mailboxes: DeskMailboxConfig[]; mode: string; cardCount?: number }> {
  const res = await axios.delete(`${API_URL}/${companyId}/desk/mailboxes/mine/${mailboxId}`, {
    params: { mode },
  });
  return res.data;
}

export async function testMyDeskMailbox(companyId: string, params: Record<string, unknown>) {
  const res = await axios.post(`${API_URL}/${companyId}/desk/mailboxes/mine/test`, params);
  return res.data;
}
